/** Copyright (c) 2017 T. Rowe Price. All rights reserved. */

import './ExternalInvestmentProductCardTable.scss';
import '../../../style/components/MobileTable.scss';

import { isFinite, isEmpty, some } from 'lodash';
import { object, array, arrayOf } from 'prop-types';
import cx from 'classnames';
import React, { Fragment } from 'react';

import { Dollar, FormattedDateTime } from 'components/shared/Text/Text';

import {
  TableColumns,
  RowLabelCell
} from 'components/shared/Table/MobileTableSupport';

import { externalAccountErrorMap } from 'services/ExternalAccountErrorCodes';

import { ReactComponent as WarningImage } from 'assets/icons/warning-triangle.svg';

import Time from 'services/Time';
import { UpdateAccountError } from 'services/ExternalAccountErrorCodes';

const UPDATE_ACCOUNT_ERROR = 'UPDATE_ACCOUNT_ERROR';
const AUTHENTICATION_ERROR = 'AUTHENTICATION_ERROR';

const isValidBalance = (b) =>
  b.details.balance.accountStatus === 0 &&
  isFinite(b.details.balance.balanceValue);
const hasValidBalances = (balances) => some(balances, isValidBalance);

const Balance = ({ balanceValue, accountStatus }) =>
  accountStatus === 0 && isFinite(balanceValue) ? (
    <Dollar
      element="td"
      className="mobile-external-investment-table-account-name-value e2e-balance-group">
      {balanceValue}
    </Dollar>
  ) : (
    <td className="mobile-external-investment-table-account-name-value currently-unavailable">
      Currently Unavailable
    </td>
  );

const HeaderRow = ({ details, validBalances }) => {
  const className =
    'header-row row-parent external-investment-account-header-group';
  const name = details.name.groupLabel;
  const { errorType, errorMessage, allExternalAccountsInGroupError } = details;

  return (
    <tr className={className}>
      <td className="mobile-external-investment-table-account-group-label">
        {(allExternalAccountsInGroupError &&
          errorType === UPDATE_ACCOUNT_ERROR) ||
        errorType === AUTHENTICATION_ERROR ? (
          <div>
            <WarningImage />
            {name}
            {errorMessage}
          </div>
        ) : (
          <div>{name}</div>
        )}
      </td>
      <Balance
        balanceValue={details.balanceValue}
        accountStatus={validBalances ? 0 : false}
      />
    </tr>
  );
};

HeaderRow.propTypes = {
  details: object,
  children: array
};

const DetailRow = ({ value, name, allExternalAccountsInGroupError }) => {
  let errorMessage = '';
  const accountLevelUpdateDateError =
    new Date(name.lastSuccessfulUpdateDate) < Time.daysBeforeNow(90);

  if (externalAccountErrorMap.has(name.accountStatus)) {
    const { message } = externalAccountErrorMap
      ? externalAccountErrorMap.get(name.accountStatus)
      : {};
    errorMessage = message;
  } else if (accountLevelUpdateDateError) {
    errorMessage = <UpdateAccountError />;
  }

  return (
    <tr
      key={name.accountNumber}
      className={`mobile-external-investment-table-row e2e-${name}-row`}>
      <RowLabelCell
        className={cx(
          'mobile-external-investment-table-row-label',
          'e2e-mobile-external-investment-table-row-label',
          'accountName-row-label',
          'e2e-accountName-row-label'
        )}>
        {!allExternalAccountsInGroupError && errorMessage && <WarningImage />}
        {`${name.accountName} - ${name.accountNumber}`}
        <p className="mobile-account-group-as-of-date">
          As of{' '}
          <FormattedDateTime>
            {' '}
            {name.lastSuccessfulUpdateDate}
          </FormattedDateTime>{' '}
          ET
        </p>
        {!allExternalAccountsInGroupError && errorMessage}
      </RowLabelCell>
      <Balance {...value} />
    </tr>
  );
};

DetailRow.propTypes = {
  value: object,
  name: object
};

const AccountRows = ({ accounts, details }) => {
  const { allExternalAccountsInGroupError } = details;

  return accounts.map(({ details }, i) => (
    <DetailRow
      key={i}
      name={details.account}
      value={details.balance}
      allExternalAccountsInGroupError={allExternalAccountsInGroupError}
    />
  ));
};

const MobileExternalInvestmentProductCardTable = ({ balances }) => (
  <div className="external-investment-product-card-table-container">
    {!isEmpty(balances) && (
      <table className="mobile-external-investment-table">
        <TableColumns baseClass="mobile-external-investment-table" />
        <tbody>
          {balances.map((institutionGroup, i) => (
            <Fragment key={i}>
              <HeaderRow
                {...institutionGroup}
                validBalances={hasValidBalances(institutionGroup.children)}
              />
              <AccountRows
                accounts={institutionGroup.children}
                {...institutionGroup}
              />
            </Fragment>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

MobileExternalInvestmentProductCardTable.propTypes = {
  balance: arrayOf(object)
};

export default MobileExternalInvestmentProductCardTable;
